// Simulator — counterfactual portfolio builder with charts and per-deal analysis.

import { useMemo, useState } from "react";
import { useAppData } from "../store";
import type { Deal } from "../types";
import { Btn, Chip, Toggle, Tabs } from "../components/Primitives";
import { Select } from "../components/Select";
import { Ic, Mark } from "../components/Icon";

// ─── Valuation data (latest confirmed post-money, mid-2026) ──────────────────
// Keyed by company name as it appears in the deal store.
// entry = valuation at the seed/A round when we first saw the deal ($M)
// last  = latest confirmed post-money valuation ($M)
// outcome = current portfolio status

type Decision = "invested" | "passed" | "rejected" | "active";

// Derive decision from live deal status
function decisionFromStatus(status: string): Decision {
  if (status === "invested") return "invested";
  if (status === "passed") return "passed";
  if (status === "rejected") return "rejected";
  return "active";
}

function monthFromDeal(deal: Deal): string {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const d = deal.timeFull ? new Date(deal.timeFull) : null;
  if (d && !isNaN(d.getTime()) && d.getFullYear() > 2010) return months[d.getMonth()] + " " + d.getFullYear();
  const c = (deal as any).createdAt ? new Date((deal as any).createdAt) : null;
  if (c && !isNaN(c.getTime()) && c.getFullYear() > 2010) return months[c.getMonth()] + " " + c.getFullYear();
  return "Jan 2025";
}

interface SimDeal {
  co: string;
  date: string;
  entry: number;
  last: number | null;
  mult: number | null;
  outcome: "active" | "exited" | "flat" | "dead" | "unknown";
  raises: { date: string; val: number }[];
  sector: string;
  stage: string;
  score: number | null;
  decision: Decision;
}

function buildSimDeals(deals: Deal[]): SimDeal[] {
  // Deduplicate by company name — keep highest score
  const byName = new Map<string, Deal>();
  for (const d of deals) {
    const existing = byName.get(d.co);
    if (!existing || (d.score ?? -1) > (existing.score ?? -1)) byName.set(d.co, d);
  }
  const result: SimDeal[] = [];
  for (const deal of byName.values()) {
    const val = deal.sim;
    if (!val) continue; // no valuation data (e.g. real firms) → not simulated
    const mult = val.last != null && val.entry > 0 ? +(val.last / val.entry).toFixed(2) : null;
    result.push({
      co: deal.co,
      date: val.date || monthFromDeal(deal),
      entry: val.entry,
      last: val.last,
      mult,
      outcome: val.outcome,
      raises: val.raises || [],
      sector: deal.sector || "Other",
      stage: deal.stage || "Unknown",
      score: deal.score,
      decision: decisionFromStatus(deal.status),
    });
  }
  return result.sort((a, b) => monthIdx(a.date) - monthIdx(b.date));
}

// ─── Utilities ───────────────────────────────────────────────────────────────
const monthIdx = (s: string): number => {
  const [m, y] = s.split(" ");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return parseInt(y, 10) * 12 + months.indexOf(m);
};

const fmtMonth = (mi: number): string => {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const y = Math.floor(mi / 12);
  const m = mi % 12;
  return months[m] + " " + String(y).slice(-2);
};

const fmtMoney = (n: number): string => {
  if (n >= 1000) {
    const b = n / 1000;
    return "$" + (Number.isInteger(b) ? b.toFixed(0) : b.toFixed(1)) + "B";
  }
  return "$" + (n < 1 ? n.toFixed(1) : n < 10 ? n.toFixed(1) : Math.round(n).toString()) + "M";
};

function pickXLabels(months: number[]): string[] {
  if (months.length <= 6) return months.map(fmtMonth);
  const step = Math.ceil(months.length / 6);
  const out: string[] = [];
  for (let i = 0; i < months.length; i += step) out.push(fmtMonth(months[i]));
  const last = fmtMonth(months[months.length - 1]);
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

function yScale(max: number, steps = 4): string[] {
  const out: string[] = [];
  for (let i = 0; i <= steps; i++) {
    out.push(fmtMoney((max * i) / steps));
  }
  return out;
}

// ─── Chart primitives (pure SVG) ─────────────────────────────────────────────
interface Padding { t: number; r: number; b: number; l: number }
interface ChartFrameProps {
  width?: number;
  height?: number;
  padding?: Padding;
  xLabels?: string[];
  yLabels?: string[];
  children: (dim: { W: number; H: number }) => React.ReactNode;
  footnote?: string;
}

function ChartFrame({
  width = 900, height = 220,
  padding = { t: 14, r: 20, b: 26, l: 52 },
  xLabels, yLabels, children, footnote,
}: ChartFrameProps) {
  const W = width - padding.l - padding.r;
  const H = height - padding.t - padding.b;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} style={{ display: "block" }}>
      <g transform={`translate(${padding.l} ${padding.t})`}>
        {(yLabels || []).map((y, i) => {
          const yp = H - (i / ((yLabels!.length - 1) || 1)) * H;
          return (
            <g key={i}>
              <line x1="0" y1={yp} x2={W} y2={yp} stroke="var(--border)" strokeDasharray={i === 0 ? "0" : "2 3"} />
              <text x="-8" y={yp + 3} textAnchor="end" fontSize="10" fill="var(--ink-3)" fontFamily="var(--mono)">{y}</text>
            </g>
          );
        })}
        {(xLabels || []).map((x, i) => {
          const xp = (i / ((xLabels!.length - 1) || 1)) * W;
          return (
            <text key={i} x={xp} y={H + 16} textAnchor="middle" fontSize="10" fill="var(--ink-3)" fontFamily="var(--mono)">{x}</text>
          );
        })}
        {children({ W, H })}
      </g>
      {footnote && <text x={padding.l} y={height - 4} fontSize="10" fill="var(--ink-4)" fontFamily="var(--mono)">{footnote}</text>}
    </svg>
  );
}

interface SeriesProps {
  points: { x: number; y: number }[];
  color?: string;
  fill?: boolean;
  sw?: number;
  W: number;
  H: number;
}

function Series({ points, color = "var(--ink)", fill = false, sw = 1.6, W, H }: SeriesProps) {
  if (!points || points.length === 0) return null;
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x * W} ${(1 - p.y) * H}`).join(" ");
  return (
    <>
      {fill && <path d={`${d} L ${W} ${H} L 0 ${H} Z`} fill={color} opacity="0.08" />}
      <path d={d} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
    </>
  );
}

function Bars({ values, color = "var(--ink)", W, H, gap = 2 }: { values: number[]; color?: string; W: number; H: number; gap?: number }) {
  if (!values.length) return null;
  const bw = (W / values.length) - gap;
  return (
    <>
      {values.map((v, i) => {
        const h = Math.max(1, v * H);
        return <rect key={i} x={i * (bw + gap)} y={H - h} width={bw} height={h} fill={color} rx="1.5" />;
      })}
    </>
  );
}

// ─── Computed series ─────────────────────────────────────────────────────────
interface ComputedSeries {
  months: number[];
  deployed: number[];
  value: number[];
  moic: number[];
  totalDeployed: number;
  totalValue: number;
  totalMoic: number;
  known: number;
  unknown: number;
}

function computeSeries(deals: SimDeal[], checkSize: number): ComputedSeries {
  if (!deals.length) return { months: [], deployed: [], value: [], moic: [], totalDeployed: 0, totalValue: 0, totalMoic: 0, known: 0, unknown: 0 };
  const sorted = [...deals].sort((a, b) => monthIdx(a.date) - monthIdx(b.date));
  const start = monthIdx(sorted[0].date);
  // Always extend to at least May 2026 so all raise events are visible
  const end = Math.max(monthIdx("May 2026"), monthIdx(sorted[sorted.length - 1].date) + 2);
  const months: number[] = [];
  for (let i = start; i <= end; i++) months.push(i);

  // Deployed grows stepwise — one check per deal at its entry month
  const deployed = months.map(m =>
    sorted.filter(d => monthIdx(d.date) <= m).length * checkSize
  );

  // Value at each month = sum of (current valuation / entry valuation) × check for each invested deal
  const value = months.map(m =>
    sorted.reduce((total, d) => {
      if (monthIdx(d.date) > m) return total;
      const events = d.raises;
      if (!events || events.length === 0) return total; // no raise data → unknown
      // Walk events in order; use the latest one at or before month m
      let curVal = events[0].val;
      for (const ev of events) {
        if (monthIdx(ev.date) <= m) curVal = ev.val;
        else break;
      }
      return total + checkSize * (curVal / d.entry);
    }, 0)
  );

  const moic = months.map((_, i) => deployed[i] ? value[i] / deployed[i] : 0);
  const totalDeployed = deployed[deployed.length - 1];
  const totalValue = value[value.length - 1];
  const totalMoic = totalDeployed ? totalValue / totalDeployed : 0;
  const known = sorted.filter(d => d.raises?.length > 0).length;
  const unknown = sorted.length - known;

  return { months, deployed, value, moic, totalDeployed, totalValue, totalMoic, known, unknown };
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: boolean }) {
  return (
    <div className="sim-stat">
      <div className="eyebrow">{label}</div>
      <div className="sim-stat-value" style={{ color: accent ? "var(--accent)" : undefined }}>{value}</div>
      {sub && <div className="sim-stat-sub">{sub}</div>}
    </div>
  );
}

function LegendDot({ label, color }: { label: string; color: string }) {
  return (
    <span className="sim-legend-dot">
      <i style={{ width: 8, height: 8, borderRadius: 4, background: color, display: "inline-block" }} />
      {label}
    </span>
  );
}

function RuleStrip({ label, op, value, onChange, onRemove, selectOptions }: {
  label: string; op: string; value: string;
  onChange: (v: string) => void;
  onRemove?: () => void;
  selectOptions?: string[];
}) {
  return (
    <div className="sim-rule">
      <span className="sim-rule-label">{label}</span>
      <span className="sim-rule-op">{op}</span>
      {selectOptions ? (
        <Select value={value} onChange={onChange} options={selectOptions} width="100%" />
      ) : (
        <input
          className="sim-rule-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {onRemove && (
        <span className="sim-rule-x" onClick={onRemove}><Ic name="x" size={12} /></span>
      )}
    </div>
  );
}

function ExclusionRow({ label, sub, children }: { label: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="sim-excl-row">
      <div style={{ flex: 1 }}>
        <div className="sim-excl-label">{label}</div>
        {sub && <div className="sim-excl-sub">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function TopContribBars({ deals, check }: { deals: SimDeal[]; check: number }) {
  const valued = deals
    .filter(d => d.mult != null)
    .map(d => ({ co: d.co, value: check * d.mult!, mult: d.mult!, dead: d.outcome === "dead" }));
  valued.sort((a, b) => b.value - a.value);
  const top = valued.slice(0, 8);
  const max = top.length ? top[0].value : 1;
  return (
    <div className="sim-contrib">
      {top.map(d => (
        <div key={d.co} className="sim-contrib-row">
          <span className="sim-contrib-name">{d.co}</span>
          <span className="sim-contrib-bar">
            <span style={{ width: (d.value / max) * 100 + "%", background: d.dead ? "var(--neg)" : "var(--accent)" }} />
          </span>
          <span className="sim-contrib-val">{fmtMoney(d.value)}</span>
          <span className="sim-contrib-sep">·</span>
          <span className="sim-contrib-mult">{d.mult.toFixed(1)}×</span>
        </div>
      ))}
    </div>
  );
}

function SectorBreakdown({ deals, check }: { deals: SimDeal[]; check: number }) {
  const by: Record<string, { count: number; deployed: number; value: number }> = {};
  deals.forEach(d => {
    if (!by[d.sector]) by[d.sector] = { count: 0, deployed: 0, value: 0 };
    by[d.sector].count++;
    by[d.sector].deployed += check;
    if (d.mult != null) by[d.sector].value += check * d.mult;
  });
  const rows = Object.entries(by).sort((a, b) => b[1].deployed - a[1].deployed);
  const maxDep = Math.max(...rows.map(r => r[1].deployed));
  return (
    <div className="sim-card">
      <div className="sim-card-head">
        <span className="sim-card-title">By sector</span>
      </div>
      <div className="sim-sector-head">
        <span>Sector</span><span></span><span>Deployed</span><span style={{ textAlign: "right" }}>Deals</span><span style={{ textAlign: "right" }}>MOIC</span>
      </div>
      {rows.map(([sector, info]) => (
        <div key={sector} className="sim-sector-row">
          <span className="sim-sector-name">{sector}</span>
          <span className="sim-sector-bar">
            <span style={{ width: (info.deployed / maxDep) * 100 + "%" }} />
          </span>
          <span className="sim-sector-val">{fmtMoney(info.deployed)}</span>
          <span className="sim-sector-val" style={{ textAlign: "right" }}>{info.count}</span>
          <span className="sim-sector-val" style={{ textAlign: "right", color: info.value ? "var(--ink)" : "var(--ink-4)" }}>
            {info.value ? (info.value / info.deployed).toFixed(2) + "×" : "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

function PerDealStrip({ deals, maxMult }: { deals: SimDeal[]; maxMult: number }) {
  return (
    <div className="sim-strip">
      {deals.slice().sort((a, b) => b.mult! - a.mult!).map(d => {
        const isOutlier = d.mult! > 10;
        const isDead = d.outcome === "dead";
        const isFlat = d.outcome === "flat";
        const w = Math.min(100, (d.mult! / maxMult) * 100);
        return (
          <div key={d.co} className="sim-strip-row">
            <span className="sim-strip-name">{d.co}</span>
            <div className="sim-strip-track">
              <div className="sim-strip-bg" />
              <div className="sim-strip-tick" style={{ left: (1 / maxMult) * 100 + "%" }} />
              <div
                className="sim-strip-fill"
                style={{
                  width: w + "%",
                  background: isDead ? "var(--neg)" : isFlat ? "var(--ink-3)" : isOutlier ? "var(--accent)" : "var(--ink)",
                }}
              />
            </div>
            <span className="sim-strip-val" style={{ color: isDead ? "var(--neg)" : isOutlier ? "var(--accent)" : "var(--ink)" }}>
              {d.mult!.toFixed(1)}×
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Views ───────────────────────────────────────────────────────────────────
function moicAxis(totalMoic: number): { cap: number; labels: string[] } {
  const step = totalMoic <= 6 ? 1 : totalMoic <= 15 ? 3 : totalMoic <= 30 ? 5 : totalMoic <= 60 ? 10 : 20;
  const cap = Math.ceil((totalMoic * 1.1) / step) * step;
  const count = Math.min(6, Math.floor(cap / step));
  const labels: string[] = [];
  for (let i = 0; i <= count; i++) labels.push(((cap / count) * i).toFixed(0) + "×");
  return { cap, labels };
}

function OverviewView({ series, deals, check }: { series: ComputedSeries; deals: SimDeal[]; check: number }) {
  const xLabels = pickXLabels(series.months);
  const yMaxValue = Math.max(...series.value, ...series.deployed);
  const { cap: moicCap, labels: moicLabels } = moicAxis(series.totalMoic || 6);
  return (
    <>
      <div className="sim-grid-2">
        <div className="sim-card">
          <div className="sim-card-head">
            <span className="sim-card-title">Deployment & value</span>
            <span style={{ flex: 1 }} />
            <LegendDot label="Deployed" color="var(--ink-3)" />
            <LegendDot label="Value" color="var(--accent)" />
          </div>
          <ChartFrame height={200} xLabels={xLabels} yLabels={yScale(yMaxValue, 4)}>
            {({ W, H }) => (
              <>
                <Series points={series.deployed.map((v, i) => ({ x: i / (series.deployed.length - 1), y: v / yMaxValue }))} color="var(--ink-3)" sw={1.4} W={W} H={H} />
                <Series points={series.value.map((v, i) => ({ x: i / (series.value.length - 1), y: v / yMaxValue }))} color="var(--accent)" sw={2} fill W={W} H={H} />
              </>
            )}
          </ChartFrame>
        </div>

        <div className="sim-card">
          <div className="sim-card-head">
            <span className="sim-card-title">Fund MOIC over time</span>
            <span style={{ flex: 1 }} />
            <span className="score" style={{ fontSize: 13, color: "var(--accent)" }}>{series.totalMoic.toFixed(2)}×</span>
          </div>
          <ChartFrame height={200} xLabels={xLabels} yLabels={moicLabels}>
            {({ W, H }) => (
              <>
                <line x1="0" y1={H * (1 - 1/moicCap)} x2={W} y2={H * (1 - 1/moicCap)} stroke="var(--border-strong)" strokeDasharray="2 3" />
                <text x={W - 4} y={H * (1 - 1/moicCap) - 4} textAnchor="end" fontSize="9" fill="var(--ink-3)" fontFamily="var(--mono)">1× baseline</text>
                <Series points={series.moic.map((v, i) => ({ x: i / (series.moic.length - 1), y: Math.min(1, v / moicCap) }))} color="var(--ink)" sw={2} fill W={W} H={H} />
              </>
            )}
          </ChartFrame>
        </div>
      </div>

      <div className="sim-card">
        <div className="sim-card-head">
          <span className="sim-card-title">Top contributors</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>by hypothetical value</span>
        </div>
        <TopContribBars deals={deals} check={check} />
      </div>

      <div className="sim-disclaimer">
        <em>This is a counterfactual, not a returns predictor.</em> {series.unknown} of {deals.length} matched deals have unknown outcomes and are excluded from the value estimate.
      </div>
    </>
  );
}

function DeploymentView({ series, deals, check }: { series: ComputedSeries; deals: SimDeal[]; check: number }) {
  const xLabels = pickXLabels(series.months);
  const yMax = Math.max(...series.deployed);
  const monthly = series.months.map((_, i) => i === 0 ? series.deployed[0] : series.deployed[i] - series.deployed[i - 1]);
  const monthlyMax = Math.max(...monthly);
  return (
    <>
      <div className="sim-card">
        <div className="sim-card-head">
          <span className="sim-card-title">Cumulative deployment</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>${(check * 1000).toLocaleString()}K per deal × {deals.length}</span>
        </div>
        <ChartFrame height={260} xLabels={xLabels} yLabels={yScale(yMax, 5)}>
          {({ W, H }) => (
            <Series points={series.deployed.map((v, i) => ({ x: i / (series.deployed.length - 1), y: v / yMax }))} color="var(--ink)" sw={2.2} fill W={W} H={H} />
          )}
        </ChartFrame>
      </div>

      <div className="sim-card">
        <div className="sim-card-head">
          <span className="sim-card-title">Monthly check pace</span>
        </div>
        <ChartFrame height={180} xLabels={xLabels} yLabels={["", "", fmtMoney(monthlyMax)]}>
          {({ W, H }) => <Bars values={monthly.map(v => v / monthlyMax)} color="var(--accent)" W={W} H={H} />}
        </ChartFrame>
      </div>

      <SectorBreakdown deals={deals} check={check} />
    </>
  );
}

function ReturnsView({ series, deals }: { series: ComputedSeries; deals: SimDeal[] }) {
  const xLabels = pickXLabels(series.months);
  const knownDeals = deals.filter(d => d.mult != null);
  const maxMult = Math.max(20, ...knownDeals.map(d => d.mult!));
  const { cap: moicCap, labels: moicLabels } = moicAxis(series.totalMoic || 6);
  return (
    <>
      <div className="sim-card">
        <div className="sim-card-head">
          <span className="sim-card-title">Fund MOIC over time</span>
          <span style={{ flex: 1 }} />
          <span className="score" style={{ fontSize: 14, color: "var(--accent)" }}>{series.totalMoic.toFixed(2)}×</span>
        </div>
        <ChartFrame height={260} xLabels={xLabels} yLabels={moicLabels}>
          {({ W, H }) => (
            <>
              <line x1="0" y1={H * (1 - 1/moicCap)} x2={W} y2={H * (1 - 1/moicCap)} stroke="var(--border-strong)" strokeDasharray="2 3" />
              <Series points={series.moic.map((v, i) => ({ x: i / (series.moic.length - 1), y: Math.min(1, v / moicCap) }))} color="var(--ink)" sw={2.4} fill W={W} H={H} />
            </>
          )}
        </ChartFrame>
      </div>

      <div className="sim-card">
        <div className="sim-card-head">
          <span className="sim-card-title">Per-deal multiples</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>0× = dead · 1× = flat · {">"}10× = trim with the outlier toggle</span>
        </div>
        <PerDealStrip deals={knownDeals} maxMult={maxMult} />
      </div>
    </>
  );
}

function DealsView({ deals, check }: { deals: SimDeal[]; check: number }) {
  return (
    <div className="sim-table">
      <div className="sim-table-head sim-table-8col">
        <span>Company</span><span>Sector</span><span>Entry date</span><span>Entry val.</span><span>Last val.</span><span>Decision</span><span>Outcome</span><span style={{ textAlign: "right" }}>Multiple</span>
      </div>
      {deals.map((d, i) => {
        const isDead = d.outcome === "dead";
        const isOutlier = d.mult != null && d.mult > 10;
        const isUnknown = d.mult == null;
        return (
          <div key={d.co} className="sim-table-row sim-table-8col" style={{ borderBottom: i < deals.length - 1 ? "1px solid var(--border)" : "none" }}>
            <span style={{ fontWeight: 500 }}>{d.co}</span>
            <span style={{ color: "var(--ink-2)" }}>{d.sector}</span>
            <span style={{ color: "var(--ink-2)" }}>{d.date}</span>
            <span className="mono">{fmtMoney(d.entry)}</span>
            <span className="mono" style={{ color: d.last == null ? "var(--ink-4)" : "var(--ink)" }}>
              {d.last == null ? "—" : fmtMoney(d.last)}
            </span>
            <span>
              <span className={"sim-decision-badge " + d.decision}>{
                d.decision === "invested" ? "Invested" :
                d.decision === "passed" ? "Passed" :
                d.decision === "rejected" ? "Rejected" : "Active"
              }</span>
            </span>
            <span>
              <span className={"sim-outcome " + d.outcome}>{
                d.outcome === "exited" ? "Exited" :
                d.outcome === "active" ? "Active" :
                d.outcome === "flat" ? "Flat" :
                d.outcome === "dead" ? "Dead" : "Unknown"
              }</span>
            </span>
            <span className="sim-table-mult">
              {!isUnknown ? (
                <>
                  <span className="sim-mult-bar">
                    <span style={{ width: Math.min(100, d.mult! * 10) + "%", background: isDead ? "var(--neg)" : isOutlier ? "var(--accent)" : "var(--ink)" }} />
                  </span>
                  <span className="score" style={{ fontSize: 13, color: isDead ? "var(--neg)" : "var(--ink)" }}>{d.mult!.toFixed(1)}×</span>
                </>
              ) : (
                <span style={{ color: "var(--ink-4)", fontFamily: "var(--mono)", fontSize: 12 }}>—</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Simulator screen ───────────────────────────────────────────────────
export function SimulatorScreen() {
  const { data } = useAppData();
  const [run, setRun] = useState(false);
  const [view, setView] = useState("Overview");

  const [decisionFilter, setDecisionFilter] = useState<"all" | Decision>("all");

  type CondField = "Stage" | "Sector" | "Score";
  interface Condition { field: CondField; value: string }
  const COND_FIELDS: CondField[] = ["Stage", "Sector", "Score"];
  const [conditions, setConditions] = useState<Condition[]>([
    { field: "Stage", value: "Pre‑seed, Seed" },
    { field: "Sector", value: "Any" },
    { field: "Score",  value: "50" },
  ]);
  const updateCond = (i: number, patch: Partial<Condition>) =>
    setConditions(cs => cs.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const removeCond = (i: number) =>
    setConditions(cs => cs.filter((_, idx) => idx !== i));
  // Add the next field type not already present, cycling through Stage→Sector→Score
  const addCond = () => {
    const used = conditions.map(c => c.field);
    const next = COND_FIELDS.find(f => !used.includes(f)) ?? "Stage";
    const defaults: Record<CondField, string> = { Stage: "Any", Sector: "Any", Score: "0" };
    setConditions(cs => [...cs, { field: next, value: defaults[next] }]);
  };

  const [check, setCheck] = useState(0.5);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [excludeUnknown, setExcludeUnknown] = useState(false);
  const [excludeSingles, setExcludeSingles] = useState(true);
  const [excludeOutliers, setExcludeOutliers] = useState(false);

  const simDeals = useMemo(() => {
    const all = [...(data?.deals ?? []), ...(data?.archive ?? [])];
    return buildSimDeals(all);
  }, [data]);

  const allCompanies = simDeals.map(d => d.co);

  const matched = useMemo(() => {
    let ds = simDeals.slice();
    if (decisionFilter !== "all") ds = ds.filter(d => d.decision === decisionFilter);
    for (const c of conditions) {
      if (c.field === "Stage" && c.value && c.value !== "Any") {
        const stages = c.value.split(",").map(s => s.trim());
        ds = ds.filter(d => stages.includes(d.stage));
      }
      if (c.field === "Sector" && c.value && c.value !== "Any") {
        ds = ds.filter(d => d.sector === c.value);
      }
      if (c.field === "Score" && c.value) {
        const min = parseInt(c.value, 10);
        if (!isNaN(min)) ds = ds.filter(d => (d.score ?? 0) >= min);
      }
    }
    if (excludeUnknown) ds = ds.filter(d => d.mult != null);
    if (excludeOutliers) ds = ds.filter(d => d.mult == null || d.mult <= 10);
    ds = ds.filter(d => !excluded.includes(d.co));
    if (excludeSingles) {
      const counts: Record<string, number> = {};
      ds.forEach(d => counts[d.sector] = (counts[d.sector] || 0) + 1);
      ds = ds.filter(d => counts[d.sector] >= 2);
    }
    return ds;
  }, [simDeals, conditions, decisionFilter, excludeUnknown, excludeOutliers, excluded, excludeSingles]);

  const series = computeSeries(matched, check);

  const toggleExclude = (co: string) =>
    setExcluded(excluded.includes(co) ? excluded.filter(c => c !== co) : [...excluded, co]);

  if (simDeals.length === 0) {
    return (
      <div className="page">
        <div className="topbar">
          <h1>Simulator</h1>
          <span className="crumb">· counterfactual portfolio builder</span>
        </div>
        <div style={{ display: "grid", placeItems: "center", flex: 1, padding: "60px 28px" }}>
          <div style={{ maxWidth: 480, textAlign: "center" }}>
            <div style={{ display: "inline-grid", placeItems: "center", width: 46, height: 46, borderRadius: 23, background: "var(--surface)", border: "1px solid var(--border)", marginBottom: 16, color: "var(--ink-3)" }}>
              <Ic name="sim" size={20} />
            </div>
            <h2 style={{ fontFamily: "var(--serif, Georgia)", fontSize: 22, fontWeight: 500, margin: "0 0 8px" }}>No portfolio data yet</h2>
            <p style={{ color: "var(--ink-2)", fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>
              The simulator builds counterfactual returns from valuation history
              attached to your deals. The demo workspace comes pre-loaded; your
              firm's simulator fills in as you record outcomes on archived deals.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="topbar">
        <h1>Simulator</h1>
        <span className="crumb">· counterfactual portfolio builder</span>
        <span className="crumb" style={{ color: "var(--ink-4)" }}>
          · valuations are seeded demo data; deals without it are excluded
        </span>
        <span className="grow" />
        {run && (
          <Tabs tabs={["Overview","Deployment","Returns","Deals"]} value={view} onChange={setView} />
        )}
      </div>

      <div className="sim-layout">
        {/* LEFT — builder panel */}
        <div className="sim-panel">
          <div className="sim-panel-inner">
            <div>
              <div className="eyebrow">Build a portfolio from deals where</div>
              <div className="sim-rules">
                {conditions.map((c, i) => (
                  <RuleStrip
                    key={i}
                    label={c.field}
                    op={c.field === "Stage" ? "is one of" : c.field === "Score" ? "is at least" : "is"}
                    value={c.value}
                    onChange={v => updateCond(i, { value: v })}
                    onRemove={() => removeCond(i)}
                    selectOptions={c.field === "Sector" ? ["Any", "AI Infrastructure", "Climate", "Bio", "Health", "Data", "Hardware", "Fintech", "Developer tools", "Consumer", "Productivity"] : undefined}
                  />
                ))}
                {conditions.length < COND_FIELDS.length && (
                  <Btn kind="ghost" size="sm" ico="plus" onClick={addCond}>Add condition</Btn>
                )}
              </div>
            </div>

            <div>
              <div className="eyebrow">Deal decision</div>
              <div className="sim-decision-chips">
                {([["all", "All deals"], ["passed", "Passed"], ["invested", "Invested"], ["rejected", "Rejected"], ["active", "Active"]] as const).map(([k, label]) => (
                  <Chip key={k} on={decisionFilter === k} onClick={() => setDecisionFilter(k as "all" | Decision)}>
                    {label}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <div className="eyebrow">Assumed check size</div>
              <div className="sim-check-display">
                <span>${(check * 1000).toLocaleString()}K per deal</span>
              </div>
              <input
                type="range" min="0.1" max="2" step="0.1"
                value={check}
                onChange={(e) => setCheck(parseFloat(e.target.value))}
                className="sim-slider"
              />
            </div>

            <div>
              <div className="eyebrow">Exclusions</div>
              <div className="sim-excl-list">
                <ExclusionRow label="Exclude unknown outcomes" sub="Drop deals we can't value yet.">
                  <Toggle on={excludeUnknown} onChange={setExcludeUnknown} />
                </ExclusionRow>
                <ExclusionRow label="Exclude one-off sectors" sub="Sectors with only a single deal.">
                  <Toggle on={excludeSingles} onChange={setExcludeSingles} />
                </ExclusionRow>
                <ExclusionRow label="Exclude > 10× outliers" sub="Trim the top tail.">
                  <Toggle on={excludeOutliers} onChange={setExcludeOutliers} />
                </ExclusionRow>
              </div>
            </div>

            <div>
              <div className="eyebrow">Exclude specific companies</div>
              <div className="sim-co-chips">
                {allCompanies.map(co => (
                  <span
                    key={co}
                    onClick={() => toggleExclude(co)}
                    className={"sim-co-chip" + (excluded.includes(co) ? " excluded" : "")}
                  >
                    {excluded.includes(co) && <Ic name="x" size={10} />}
                    {co}
                  </span>
                ))}
              </div>
            </div>

            <Btn kind="accent" onClick={() => setRun(true)} style={{ width: "100%", justifyContent: "center" }}>
              Run simulation →
            </Btn>
          </div>
        </div>

        {/* RIGHT — results */}
        <div className="sim-results">
          {!run ? (
            <div className="empty" style={{ paddingTop: 100 }}>
              <div className="mark"><Mark size={28} /></div>
              <h3>Build a counterfactual.</h3>
              <p>Pick the conditions on the left and run. We'll match historical deals against them, then estimate what could have been.</p>
            </div>
          ) : matched.length === 0 ? (
            <div className="empty" style={{ paddingTop: 100 }}>
              <div className="mark"><Mark size={28} /></div>
              <h3>No deals match.</h3>
              <p>Loosen the filters on the left to surface more historical deals.</p>
            </div>
          ) : (
            <div className="sim-results-inner">
              <div className="sim-stats">
                <StatCard label="Matched deals" value={matched.length} sub={`${series.known} known · ${series.unknown} unknown`} />
                <StatCard label="Deployed" value={fmtMoney(series.totalDeployed)} sub={`${(check * 1000).toLocaleString()}K per deal`} />
                <StatCard label="Current value" value={fmtMoney(series.totalValue)} sub="At last priced round" />
                <StatCard label="Fund MOIC" value={series.totalMoic.toFixed(2) + "×"} sub="Excludes unknowns" accent />
              </div>

              {view === "Overview" && <OverviewView series={series} deals={matched} check={check} />}
              {view === "Deployment" && <DeploymentView series={series} deals={matched} check={check} />}
              {view === "Returns" && <ReturnsView series={series} deals={matched} />}
              {view === "Deals" && <DealsView deals={matched} check={check} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
